import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Container, Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { colors } from '../../styled/StyledComponents';
import { RESONANCE_URL } from '../../../config/links';

const HeroRoot = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  backgroundColor: colors.dark,
});

const BackgroundVideo = styled('video')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 0,
});

const Overlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.8) 100%)',
  zIndex: 1,
});

const PrimaryCta = styled(Button)({
  backgroundColor: colors.primary,
  color: '#FFFFFF',
  padding: '14px 32px',
  borderRadius: '4px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  letterSpacing: '0.3px',
  boxShadow: '0 8px 24px rgba(12, 132, 102, 0.35)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#0a6f55',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 32px rgba(12, 132, 102, 0.5)',
  },
});

const SecondaryCta = styled(Button)({
  color: '#FFFFFF',
  padding: '14px 8px',
  fontSize: '1rem',
  fontWeight: 500,
  textTransform: 'none',
  letterSpacing: '0.3px',
  '&:hover': {
    backgroundColor: 'transparent',
    color: colors.primary,
  },
});

const ScrollDown = styled(Box)({
  position: 'absolute',
  bottom: 32,
  left: '50%',
  transform: 'translateX(-50%)',
  color: 'rgba(255,255,255,0.7)',
  cursor: 'pointer',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  fontSize: '0.8rem',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  transition: 'color 0.3s ease',
  '&:hover': {
    color: '#FFFFFF',
  },
});

const HeroSection = ({ scrollToSection }) => {
  const { t } = useTranslation();

  const handleScrollDown = () => scrollToSection('pillars');

  return (
    <HeroRoot>
      <BackgroundVideo autoPlay muted loop playsInline>
        <source src="/videos/bg.mp4" type="video/mp4" />
      </BackgroundVideo>
      <Overlay />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
        >
          <Box sx={{ maxWidth: '900px', py: { xs: 14, md: 10 } }}>
            <Typography
              component="h1"
              sx={{
                color: '#FFFFFF',
                fontWeight: 800,
                fontSize: { xs: '3rem', sm: '3.5rem', md: '4rem', lg: '4.5rem' },
                lineHeight: 1.1,
                mb: 3,
                letterSpacing: '-0.02em',
              }}
            >
              {t('landing.hero.title.line1')}
              <Box
                component="span"
                sx={{ display: 'block', color: colors.primary }}
              >
                {t('landing.hero.title.line2')}
              </Box>
            </Typography>

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                lineHeight: 1.55,
                mb: 5,
                maxWidth: '560px',
              }}
            >
              {t('landing.hero.subtitle')}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <PrimaryCta
                component="a"
                href={RESONANCE_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowForwardIcon />}
              >
                {t('landing.hero.ctaPrimary')}
              </PrimaryCta>
              <SecondaryCta onClick={handleScrollDown} endIcon={<ArrowDownwardIcon />}>
                {t('landing.hero.ctaSecondary')}
              </SecondaryCta>
            </Stack>
          </Box>
        </motion.div>
      </Container>

      <ScrollDown onClick={handleScrollDown} role="button" aria-label="Scroll to pillars">
        <ArrowDownwardIcon fontSize="small" />
      </ScrollDown>
    </HeroRoot>
  );
};

HeroSection.propTypes = {
  scrollToSection: PropTypes.func.isRequired,
};

export default HeroSection;
